import { useMemo } from "react";
import {
  BatchItem,
  BatchItemStatus,
  PayeeImportBatchItemData
} from "@wingspanhq/wingspan-api-sdk";
import { queriesBatches } from "@query";

const HEADERS = [
  { label: "Wingspan user ID", key: "payeeId" },
  { label: "External contractor ID", key: "externalId" },
  { label: "Email", key: "email" },
  { label: "Name", key: "displayName" },
  { label: "Business name", key: "doingBusinessAs" },
  { label: "Phone number", key: "phone" },
  { label: "Error", key: "error" }
];

/**
 * The rows v3 could not import, as a CSV the user can correct and re-upload.
 *
 * v1 reconstructed the original spreadsheet columns from the batch item's
 * labels; v3 stores the item as the typed `PayeeImportBatchItemData` it was
 * posted as, so the export mirrors that shape plus `statusReason`. Custom
 * fields are left out: their column headings are per-account, and the user
 * corrects them against their own spreadsheet rather than this file.
 */
export function useDownloadContractorImportErrorsCSV(batchId: string) {
  const queryFailedItems = queriesBatches.listBatchItems.useQueryAll({
    batchId,
    filter: { status: { anyOf: [BatchItemStatus.FAILED] } }
  });

  const failedItems = useMemo(
    () => queryFailedItems.flatData ?? [],
    [queryFailedItems.flatData]
  );

  const errorsCSVFileProps = useMemo(() => {
    const data = failedItems.map((batchItem: BatchItem) => {
      const itemData = batchItem.data as PayeeImportBatchItemData;

      return {
        payeeId: itemData?.payeeId ?? "",
        externalId: itemData?.externalId ?? "",
        email: itemData?.email ?? "",
        displayName: itemData?.displayName ?? "",
        doingBusinessAs: itemData?.doingBusinessAs ?? "",
        phone: itemData?.phone ?? "",
        error: batchItem.statusReason ?? ""
      };
    });

    return {
      filename: `import-errors-${batchId}.csv`,
      headers: HEADERS,
      data
    };
  }, [failedItems, batchId]);

  return { errorsCSVFileProps, hasErrors: failedItems.length > 0 };
}
